import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./leaderboard.css";
import { setBackButtonUrl } from '../uiSlice';
import {
  getThemeAssetUrl,
  toCssUrlValue,
  themeBackgroundKeyForViewport,
} from "../../functions/themeAssets";

const formatCategory = (key) =>
  (key || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const formatTimestamp = (value) => {
  if (value == null || value === "") return "";

  try {
    if (typeof value === "object") {
      const seconds = value.seconds ?? value._seconds;
      const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
      if (seconds != null) {
        const ms = Number(seconds) * 1000 + Number(nanoseconds) / 1e6;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }
      if (typeof value.toDate === "function") {
        const d = value.toDate();
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }
    }

    if (typeof value === "number") {
      const ms = value > 1e12 ? value : value * 1000; // seconds vs milliseconds
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
    }

    if (typeof value === "string") {
      const asNum = Number(value);
      if (!Number.isNaN(asNum)) {
        const ms = asNum > 1e12 ? asNum : asNum * 1000;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }

      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
    }
  } catch {
    // ignore and fallback to raw string
  }

  return String(value);
};

function Leaderboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user } = useSelector((state) => state.auth);

  const [list, setList] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;


  useEffect(() => {
    dispatch(setBackButtonUrl("/fullhouse"));
  }, [status, user]);

  const showNameColumn = list.some(
    (row) => row?.name != null && String(row.name).trim() !== "" && row.name !== "null null"
  );
  const emailHeaderLabel = list.some((row) =>
    String(row?.email ?? "").includes("@")
  )
    ? "Email"
    : "Employee Id";
  const colSpan = showNameColumn ? 5 : 4;

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setList(data.data);
          setFilteredResults(data.data);
          setCurrentPage(1); // Reset to first page when new data loads
        } else {
          setError(data.error || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken]);

  // Apply search filter automatically when searchQuery changes
  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      setFilteredResults(list);
    } else {
      const filtered = list.filter((row) => {
        const name = row.name || "";
        const email = row.email || "";
        const category = row.category || "";
        const rank = row.rank?.toString() || "";
        
        return (
          name.toLowerCase().includes(query) ||
          email.toLowerCase().includes(query) ||
          category.toLowerCase().includes(query) ||
          rank.includes(query)
        );
      });
      setFilteredResults(filtered);
    }
    setCurrentPage(1); // Reset to first page after search
  }, [searchQuery, list]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredResults.slice(startIndex, endIndex);

  const buttonColor = themeData?.button_color ?? "#6b91e8";
  const buttonTextColor = themeData?.button_text_color ?? "#ffffff";
  const textColor = themeData?.text_color ?? "#333";
  const bgKey = themeBackgroundKeyForViewport(themeData);
  const bgImage = bgKey ? getThemeAssetUrl(bgKey) : "";

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div
      className="leaderboard-page"
      style={bgImage ? { backgroundImage: toCssUrlValue(bgImage) } : {}}
    >

      <div className="leaderboard-container">
        <h2 className="leaderboard-title" style={{ color: textColor }}>
          Leaderboard
        </h2>

        <div className="leaderboard-search">
          <div className="leaderboard-search-input-wrap">
            <input
              type="text"
              className="leaderboard-search-input"
              placeholder={
                emailHeaderLabel === "Email"
                  ? "Search by name, email, category, or rank..."
                  : "Search by name, employee id, category, or rank..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ color: textColor }}
            />
            {searchQuery.trim() ? (
              <button
                type="button"
                className="leaderboard-search-btn"
                onClick={handleClearSearch}
                aria-label="Clear search"
                title="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="leaderboard-loading">Loading...</div>
        ) : error ? (
          <div className="leaderboard-error">{error}</div>
        ) : (
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  {showNameColumn ? <th>Name</th> : null}
                  <th>{emailHeaderLabel}</th>
                  <th>Category</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="leaderboard-empty">
                      No claims yet.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((row) => (
                    <tr key={`${row.rank}-${row.timestamp}-${row.category}`}>
                      <td>{row.rank}</td>
                      {showNameColumn ? <td>{(!row.name || row.name === "null null") ? "—" : row.name}</td> : null}
                      <td>{row.email}</td>
                      <td>{formatCategory(row.category)}</td>
                      <td>{row.timestamp ? formatTimestamp(row.timestamp) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {totalPages > 1 && (
              <div className="leaderboard-pagination">
                <button
                  className="leaderboard-pagination-btn"
                  style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <span className="leaderboard-pagination-info" style={{ color: textColor }}>
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  className="leaderboard-pagination-btn"
                  style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
